import { FC, PropsWithChildren } from 'react';
import { FormControl, FormLabel, Stack, styled } from '@mui/material';
import { FontWeights } from 'theme/Typography';
import {
  COLOR_BLACK,
  COLOR_DARK_GREEN,
  COLOR_GRAY_150,
  COLOR_LIGHT_GREEN,
  COLOR_LIGHT_PINK,
  COLOR_WHITE,
} from 'theme/variables';

import { InputField, InputFieldState } from './InputField';

export type CalculatedFees = {
  txnFees: number;
  processingFees: number;
  shipping: number;
  total: number;
};

type CalculatedFeesFormProps = {
  fees: CalculatedFees;
  active?: InputFieldState;
  prefixId: string;
};

const StyledInputField = styled(InputField, { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>(
  ({ active }) => ({
    '& .MuiOutlinedInput-root': {
      padding: '10px 20px',
      fontSize: 'clamp(0.875rem, 0.7188rem + 0.6944vw, 1.5rem)',
      maxWidth: '220px',
      borderRadius: '10px',
      border: `0.5px solid ${COLOR_BLACK}`,
      cursor: 'default',
      color: active ? COLOR_DARK_GREEN : COLOR_GRAY_150,
    },
  }),
);

const StyledTotalInputField = styled(StyledInputField, { shouldForwardProp: (prop) => prop !== 'active' })<{
  active: boolean;
}>(({ active }) => ({
  '& .MuiOutlinedInput-root': {
    maxWidth: '259px',
    borderRadius: '16px',
    border: 'none',
    backgroundColor: COLOR_BLACK,
    fontSize: active
      ? 'clamp(1.5rem, 1.0938rem + 1.8056vw, 3.125rem)'
      : 'clamp(1.125rem, 0.7813rem + 1.5278vw, 2.5rem)',
    color: active ? COLOR_LIGHT_GREEN : COLOR_WHITE,
    height: 'calc(clamp(1.5rem, 1.0938rem + 1.8056vw, 3.125rem) + 20px)',
  },
}));

export const formatNumberValue = (value: number): string => {
  return value.toFixed(2);
};

export const CalculatedFeesForm: FC<PropsWithChildren<CalculatedFeesFormProps>> = ({
  children,
  fees,
  prefixId,
  active = InputFieldState.OFF,
}) => {
  return (
    <Stack alignItems="center" justifyContent="end" gap="5px">
      {children}
      <FormControl
        component="fieldset"
        sx={{
          backgroundColor: COLOR_WHITE,
          maxWidth: '300px',
          padding: { xs: '20px 5px 7.5px', sm: '20px 10px 15px', lg: '41.45px 20.5px 27.5px' },
          borderRadius: '16px',
          border: '1px solid rgba(200,200,200,0.5)',
          gap: { xs: '25px', sm: '40px', md: '60px', lg: '81px' },
        }}
      >
        <FormLabel component="legend" sx={{ display: 'none' }}>
          {prefixId}
        </FormLabel>
        <Stack sx={{ gap: { xs: '18px', md: '11.5px' } }}>
          <StyledInputField
            active={active === InputFieldState.OFF}
            id={`${prefixId}-txn-fee`}
            currentInputState={active}
            value={formatNumberValue(fees.txnFees)}
            externalLabel={{ content: 'Transaction fee', externalOffset: 20.5 }}
          />
          <StyledInputField
            active={active === InputFieldState.OFF}
            id={`${prefixId}-processing-fee`}
            currentInputState={active}
            value={formatNumberValue(fees.processingFees)}
            externalLabel={{ content: 'Processing fee', externalOffset: 20.5 }}
          />
          <StyledInputField
            active={active === InputFieldState.OFF}
            id={`${prefixId}-shipping`}
            currentInputState={active}
            value={formatNumberValue(fees.shipping)}
            externalLabel={{ content: 'Shipping', externalOffset: 20.5 }}
          />
        </Stack>
        <StyledTotalInputField
          active={active === InputFieldState.OFF}
          id={`total-${prefixId}-cost`}
          withStartAdornment={{
            fontWeight: FontWeights.fontWeightRegular,
            fontSize: 'clamp(1rem, 0.6875rem + 1.3889vw, 2.25rem)',
            color: COLOR_LIGHT_PINK,
            fontFamily: '"Roboto", sans-serif',
          }}
          currentInputState={active}
          value={formatNumberValue(fees.total)}
          externalLabel={{ content: 'TOTAL', externalOffset: 20.5 }}
        />
      </FormControl>
    </Stack>
  );
};
